import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";

// Custom hooks
import useCostsData from "../../hooks/DeliveryCenterCosts/useCostsData";
import useFilters from "../../hooks/DeliveryCenterCosts/useFilters";
import useAlert from "../../hooks/DeliveryCenterCosts/useAlert";

// Components
import CostsHeader from "./CostsHeader";
import CostsFilters from "./CostsFilters";
import CostsTable from "./CostsTable";
import CustomAlert from "../ui/CustomAlert";

// Modals
import CostModal from "./modals/CostModal";
import CategoryModal from "./modals/CategoryModal";
import SupplierModal from "./modals/SupplierModal";

// Utils
import { downloadFile, generateCsvContent } from "../../utils/files";
import CustomConfirmationDialog from "../dialogs/CustomConfirmationDialog";

/**
 * Main costs page component
 * @returns {JSX.Element} Costs page component
 */
const CostsPage = () => {
  const {
    costs,
    categories,
    suppliers,
    filteredCosts,
    setFilteredCosts,
    loading,
    error,
    fetchAllData,
    deleteCost,
    getCategoryName,
    getSupplierName,
    getExportData,
  } = useCostsData();

  const {
    filters,
    filterOpen,
    handleFilterChange,
    resetFilters,
    toggleFilters,
    applyFilters,
  } = useFilters(costs, setFilteredCosts);

  const { alertOpen, alertMessage, alertSeverity, showAlert, closeAlert } =
    useAlert();

  // Modal states
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Confirmation dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState(null);

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Apply filters when costs or filter criteria change
  useEffect(() => {
    if (costs.length > 0) {
      applyFilters(getCategoryName, getSupplierName);
    }
  }, [costs, filters]);

  // Error handling
  useEffect(() => {
    if (error) {
      showAlert(error, "error");
    }
  }, [error]);

  // Modal handlers
  const openCostModal = (cost = null) => {
    setEditMode(!!cost);
    setCurrentItem(cost);
    setCostModalOpen(true);
  };

  const openCategoryModal = (category = null) => {
    setEditMode(!!category);
    setCurrentItem(category);
    setCategoryModalOpen(true);
  };

  const openSupplierModal = (supplier = null) => {
    setEditMode(!!supplier);
    setCurrentItem(supplier);
    setSupplierModalOpen(true);
  };

  const closeAllModals = () => {
    setCostModalOpen(false);
    setCategoryModalOpen(false);
    setSupplierModalOpen(false);
    setCurrentItem(null);
    setEditMode(false);
  };

  const handleModalSuccess = (message) => {
    showAlert(message, "success");
    fetchAllData();
  };

  // Confirmation dialog handlers
  const openConfirmDialog = (costId) => {
    setCostToDelete(costId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setCostToDelete(null);
  };

  const confirmDeleteCost = async () => {
    if (!costToDelete) {
      return;
    }

    const success = await deleteCost(costToDelete);
    if (success) {
      showAlert("Cost deleted successfully!", "success");
      fetchAllData();
    }
  };

  // Delete cost handler
  const handleDeleteCost = (costId) => {
    openConfirmDialog(costId);
  };

  // CSV export
  const exportCsv = () => {
    const { headers, data } = getExportData();
    const csvContent = generateCsvContent(headers, data);
    const filename = `exported-costs-${dayjs().format("YYYY-MM-DD")}.csv`;
    downloadFile(csvContent, filename);
  };

  // Format category and supplier options for select fields
  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const supplierOptions = suppliers.map((sup) => ({
    value: sup.id,
    label: sup.name,
  }));

  return (
    <Box sx={{ p: 6 }}>
      {/* Alert */}
      <CustomAlert
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        onClose={closeAlert}
      />

      {/* Confirmation Dialog */}
      <CustomConfirmationDialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDeleteCost}
        title="Deletar Nota?"
        dialogContent="Tem certeza que deseja deletar a nota?"
        confirmText="Deletar"
        cancelText="Não"
      />

      {/* Page Header */}
      <CostsHeader
        onAddCost={() => openCostModal()}
        onAddCategory={() => openCategoryModal()}
        onAddSupplier={() => openSupplierModal()}
      />

      <Box sx={{ p: 2 }}>
        {/* Filters Section */}
        <CostsFilters
          filters={filters}
          filterOpen={filterOpen}
          categoryOptions={categoryOptions}
          supplierOptions={supplierOptions}
          handleFilterChange={handleFilterChange}
          resetFilters={resetFilters}
          toggleFilters={toggleFilters}
          applyFilters={() => applyFilters(getCategoryName, getSupplierName)}
          refreshData={fetchAllData}
          exportCsv={exportCsv}
        />

        {/* Data Table */}
        <CostsTable
          costs={filteredCosts}
          getCategoryName={getCategoryName}
          getSupplierName={getSupplierName}
          onEditCost={openCostModal}
          onDeleteCost={handleDeleteCost}
          onAddCost={() => openCostModal()}
          loading={loading}
        />
      </Box>

      {/* Modals */}
      <CostModal
        isOpen={costModalOpen}
        onClose={closeAllModals}
        editMode={editMode}
        initialData={currentItem}
        categories={categories}
        suppliers={suppliers}
        onSuccess={handleModalSuccess}
      />

      <CategoryModal
        isOpen={categoryModalOpen}
        onClose={closeAllModals}
        editMode={editMode}
        initialData={currentItem}
        onSuccess={handleModalSuccess}
      />

      <SupplierModal
        isOpen={supplierModalOpen}
        onClose={closeAllModals}
        editMode={editMode}
        initialData={currentItem}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
};

export default CostsPage;
